'use client'

import { Button } from '@/General/Button'
import { toastEmitter } from '@/General/Toast'
import { useCookieValue } from '@/Cookies/useCookies'
import { fetchData } from '@/lib/fetcher'
import { useStore } from '@/store'
import { tracking } from '@/tracking'
import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'

const grantExplanationAccessMutation = `
  mutation GrantExplanationAccess(
    $customerId: Int!
    $brandId: Int!
    $productSlug: String!
    $accessCode: String
  ) {
    grantExplanationAccess(
      customerId: $customerId
      brandId: $brandId
      productSlug: $productSlug
      accessCode: $accessCode
    ) {
      success
      message
      alreadyHadAccess
      productName
    }
  }
`

type GrantExplanationAccessVariables = {
  customerId: number
  brandId: number
  productSlug: string
  accessCode?: string | null
}

type GrantExplanationAccessResponse = {
  grantExplanationAccess: {
    success: boolean
    message?: string | null
    alreadyHadAccess: boolean
    productName?: string | null
  }
}

type ExplanationFreeDownloadButtonProps = {
  productSlug: string
  redirectPath: string
  kind?: 'download' | 'app'
  className?: string
  accessCode?: string | null
  productName?: string | null
}

export const ExplanationFreeDownloadButton = ({
  productSlug,
  redirectPath,
  kind = 'download',
  className,
  accessCode,
  productName,
}: ExplanationFreeDownloadButtonProps) => {
  const router = useRouter()
  const user = useStore(state => state.user)
  const ecommBrandId = useStore(state => state.ecommBrandId)
  const brandCookie = useCookieValue('vi-brand')
  const brandId = ecommBrandId ?? (brandCookie === 'VANISHING_INC_UK' ? 2 : 1)
  const itemLabel = kind === 'app' ? 'app' : 'download'
  const destination = kind === 'app' ? '/account/apps/' : '/account/downloads/'
  const grantAccessCode = accessCode?.trim()

  const { mutate: grantExplanationAccess, isPending } = useMutation({
    mutationFn: (variables: GrantExplanationAccessVariables) =>
      fetchData<
        GrantExplanationAccessResponse,
        GrantExplanationAccessVariables
      >(grantExplanationAccessMutation, variables)(),
    onSuccess: data => {
      const result = data.grantExplanationAccess
      const trackedName = result.productName ?? productName

      if (!result.success) {
        toastEmitter.emit('toast', {
          title:
            result.message ||
            `We could not add this ${itemLabel} to your account. Please try again.`,
          variant: 'error',
        })
        return
      }

      if (trackedName) {
        tracking.track(
          kind === 'app'
            ? 'Registered for app from explanation page'
            : 'Registered for free download from explanation page',
          {
            [kind === 'app' ? 'App name' : 'Free download name']: trackedName,
            Source: 'explanation-page',
          }
        )
      }

      const name = trackedName ? `&name=${encodeURIComponent(trackedName)}` : ''
      const already = result.alreadyHadAccess ? '&already=1' : ''
      router.push(`${destination}?added=1${name}${already}`)
    },
    onError: (err: Error) => {
      toastEmitter.emit('toast', {
        title: err.message || 'Something went wrong. Please try again.',
        variant: 'error',
      })
    },
  })

  const buttonLabel =
    kind === 'app'
      ? 'Add this app to your account'
      : 'Add this download to your account'
  const pendingLabel = kind === 'app' ? 'Adding app...' : 'Adding...'

  if (!user?.id) {
    return (
      <Button
        href={`/account/login?redirect=${encodeURIComponent(redirectPath)}`}
        color="green"
        size="md"
        className={className}
      >
        {buttonLabel}
      </Button>
    )
  }

  return (
    <Button
      type="button"
      color="green"
      size="md"
      className={className}
      disabled={isPending}
      onClick={() => {
        grantExplanationAccess({
          customerId: user.id,
          brandId,
          productSlug,
          accessCode: grantAccessCode || null,
        })
      }}
    >
      {isPending ? pendingLabel : buttonLabel}
    </Button>
  )
}
